<!--
Perform Maintenance Execution Modal Form
- Upsert form for recording and processing completed maintenance operations.
- Two-panel collapsible widget layout consistent with Enterprise V2 Move & Reserve forms.
- Left Panel: Maintenance Details (Perform Date-Time, Asset ComboBox with infinite scroll lazy loading, Open Schedule Selector, Performed By Technician, Automatic Recurrence Advancement Checkbox).
- Right Panel: Costs & Notes (Labor Cost, Parts Cost, Calculated Total Cost, Notes, and Schedule Instructions).
- Alerts: Overdue / Checkout Blacklist notice banner if asset has delinquent maintenance schedules.
- Sub-dialogs: Asset selection grid lookup, Person selection grid lookup.
-->
@if (_active) {
  <kendo-window
    #mainWindow
    title="Perform Maintenance"
    (close)="onCancel()"
    (stateChange)="_onStateChanges($event)"
    class="fullWidthFormForm"
    (keydown.enter)="$event.preventDefault()"
    >
    <form novalidate [formGroup]="_form" (keydown.enter)="$event.preventDefault()" class="form_style">
      <!-- Top Action Header matching Reserve Popup Design -->
      <div class="row">
        <div class="col-12">
          <div class="reservation-section-header">
            <div class="reservation-section-header-title">
              <span>{{ isPerformAction ? 'Perform Maintenance' : (_pageState === PageState.VIEW ? 'View Performed Maintenance' : (_pageState === PageState.EDIT ? 'Edit Performed Maintenance' : 'Add Maintenance Transaction')) }}</span>
            </div>
            <div class="header-actions">
              <!-- Close Button -->
              <button class="steps_buttons_class" kendoButton type="button" (click)="onCancel()">
                <span>
                  <img [src]="'/assets/menu/' + _theme + '/close-schedule-report-icon-green.svg'" />
                </span>
                Close
              </button>
              <!-- Perform Button (from grid perform action) -->
              @if (isPerformAction) {
                <button
                  class="steps_buttons_class"
                  kendoButton
                  type="button"
                  (click)="_onSave('saveAndClose')"
                  [disabled]="isSubmitting"
                  >
                  <span>
                    <img [src]="'/assets/menu/' + _theme + '/proccess-icon.svg'" />
                  </span>
                  Perform
                </button>
              }
              <!-- Save & Close Button (Add / Edit mode) -->
              @if (!isPerformAction && _pageState !== PageState.VIEW) {
                <button
                  class="steps_buttons_class"
                  kendoButton
                  type="button"
                  (click)="_onSave('saveAndClose')"
                  [disabled]="isSubmitting"
                  >
                  <span>
                    <img [src]="'/assets/menu/' + _theme + '/save-icon-group.svg'" />
                  </span>
                  Save &amp; Close
                </button>
              }
              <!-- Save Button (Add / Edit mode) -->
              @if (!isPerformAction && _pageState !== PageState.VIEW) {
                <button
                  class="steps_buttons_class"
                  kendoButton
                  type="button"
                  (click)="_onSave('save')"
                  [disabled]="isSubmitting"
                  >
                  <span>
                    <img [src]="'/assets/menu/' + _theme + '/save-icon-group.svg'" />
                  </span>
                  Save
                </button>
              }
            </div>
          </div>
        </div>
      </div>
      <!-- Overdue Blacklist Notice Banner -->
      @if (blacklistInfo) {
        <div class="alert alert-warning d-flex align-items-center mb-3 mx-3 mt-2">
          <span class="k-icon k-i-warning me-2 text-warning" style="font-size: 1.5rem;"></span>
          <div>
            <strong>Overdue Maintenance Notice:</strong> {{ blacklistInfo.message }}
            @if (blacklistInfo.overdueSchedules?.length) {
              <span>
                ({{ blacklistInfo.overdueSchedules.length }} overdue schedule(s) pending).
              </span>
            }
          </div>
        </div>
      }
      <!-- Form of Transaction: Two-Panel Widget Layout -->
      <div class="row no-row-margin">
        <!-- Left Panel: Maintenance Details -->
        <div class="col-12 col-lg-6 small-pad-right">
          <div [ngClass]="isDetailsExpanded ? 'transactionWidget' : 'transactionWidget-collapse'">
            <div class="row">
              <div class="col-12 sectionheader special_style_formgroup">
                <span class="titleStandardStyle">Maintenance Details</span>
                <span
                  [ngClass]="isDetailsExpanded ? 'k-icon k-i-sort-asc-sm' : 'k-icon k-i-sort-desc-sm'"
                  class="float-right pointer-click"
                  (click)="isDetailsExpanded = !isDetailsExpanded"
                ></span>
              </div>
              @if (isDetailsExpanded) {
                <!-- Maintenance ID # -->
                <div class="col-12 margin-top-2">
                  <div class="form-group special_style_formgroup">
                    <div class="row align-items-center">
                      <div class="col-3 col-xl-2">
                        <label class="control-label">Maintenance ID # *</label>
                      </div>
                      <div class="col-9 col-xl-10 transaction-move-input-fields">
                        <input
                          type="text"
                          class="k-textbox writebox bordered newFormControlWidth"
                          formControlName="maintenanceCode"
                          placeholder="Maintenance ID #"
                          required
                          />
                          @if (actionType !== 'VIEW') {
                            <a
                              class="cursor-pointer asap-input-icon ms-2"
                              (click)="_generateBarcodeClick()"
                              title="Auto-generate Maintenance ID #"
                              >
                              <img class="searchIcon" [src]="'/assets/menu/' + (_theme || 'default') + '/auto-generate-icon.svg'" />
                            </a>
                          }
                        </div>
                      </div>
                      @if (_form.get('maintenanceCode')?.touched && _form.get('maintenanceCode')?.errors?.['required']) {
                        <div
                          class="row"
                          >
                          <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                            Maintenance ID # is required.
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                  <!-- Asset -->
                  <div class="col-12 margin-top-2">
                    <div class="form-group special_style_formgroup">
                      <div class="row align-items-center">
                        <div class="col-3 col-xl-2">
                          <label class="control-label">Asset *</label>
                        </div>
                        <div class="col-9 col-xl-10 transaction-move-input-fields">
                          <kendo-combobox
                            #Kendo_Combobox_Assets
                            class="bordered newFormControlWidth"
                            [data]="assetsDataFiltered"
                            [suggest]="true"
                            [filterable]="true"
                            placeholder="Choose an Asset"
                            formControlName="assetId"
                            textField="assetNumber"
                            valueField="id"
                            [valuePrimitive]="true"
                            (filterChange)="handleAssetFilter($event)"
                            (opened)="scrollCombobox($event)"
                            (close)="onComboboxClosed()"
                            required
                            >
                            <ng-template kendoComboBoxNoDataTemplate>
                              <div class="stocksettEnabledContainer">
                                <span class="missing-item-txt">No asset found.</span>
                              </div>
                            </ng-template>
                            <ng-template kendoComboBoxItemTemplate let-dataItem>
                              <div class="transactions-receive-stock-dropdown-item">
                                <div class="truncate">
                                  <strong>{{ dataItem.assetNumber }}</strong>
                                  @if (dataItem.assetName || dataItem.name) {
                                    <span> | {{ dataItem.assetName || dataItem.name }}</span>
                                  }
                                </div>
                              </div>
                            </ng-template>
                          </kendo-combobox>
                        </div>
                      </div>
                      @if (_form.get('assetId')?.touched && _form.get('assetId')?.errors?.['required']) {
                        <div
                          class="row"
                          >
                          <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                            Asset is required.
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                  <!-- Maintenance Type -->
                  <div class="col-12 margin-top-2">
                    <div class="form-group special_style_formgroup">
                      <div class="row align-items-center">
                        <div class="col-3 col-xl-2">
                          <label class="control-label">Maintenance Type *</label>
                        </div>
                        <div class="col-9 col-xl-10 transaction-move-input-fields">
                          <kendo-combobox
                            #Kendo_Combobox_MaintenanceTypes
                            class="bordered newFormControlWidth"
                            formControlName="maintenanceTypeId"
                            [data]="maintenanceTypesFiltered"
                            [textField]="'maintenanceTypeCode'"
                            [valueField]="'id'"
                            [valuePrimitive]="true"
                            [suggest]="true"
                            [filterable]="true"
                            (filterChange)="handleMaintenanceTypeFilter($event)"
                            placeholder="Choose a Maintenance Type"
                            required
                            >
                            <ng-template kendoComboBoxNoDataTemplate>
                              <div class="stocksettEnabledContainer">
                                <span class="missing-item-txt">No maintenance type found.</span>
                              </div>
                            </ng-template>
                            <ng-template kendoComboBoxItemTemplate let-dataItem>
                              <div class="transactions-receive-stock-dropdown-item">
                                <div class="truncate">
                                  <strong>{{ dataItem.maintenanceTypeCode }}</strong>
                                  @if (dataItem.maintenanceTypeName) {
                                    <span> | {{ dataItem.maintenanceTypeName }}</span>
                                  }
                                </div>
                              </div>
                            </ng-template>
                          </kendo-combobox>
                        </div>
                      </div>
                      @if (_form.get('maintenanceTypeId')?.touched && _form.get('maintenanceTypeId')?.errors?.['required']) {
                        <div
                          class="row"
                          >
                          <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                            Maintenance Type is required.
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                  <!-- Date vs Non Date Mode Radio Buttons -->
                  <div class="col-12 margin-top-2">
                    <div class="form-group special_style_formgroup">
                      <div class="row align-items-center">
                        <div class="col-3 col-xl-2">
                          <!-- Radio label spacer matching screenshot -->
                        </div>
                        <div class="col-9 col-xl-10">
                          <div class="transaction-move-grid-top-actions d-flex align-items-center">
                            <div class="transaction-move-grid-top-actions-radio me-4">
                              <input
                                type="radio"
                                id="perform-mode-date"
                                name="timeInterval"
                                [value]="true"
                                formControlName="timeInterval"
                                kendoRadioButton
                                />
                                <label class="k-radio-label ms-1" for="perform-mode-date">Date</label>
                              </div>
                              <div class="transaction-move-grid-top-actions-radio">
                                <input
                                  type="radio"
                                  id="perform-mode-nondate"
                                  name="timeInterval"
                                  [value]="false"
                                  formControlName="timeInterval"
                                  kendoRadioButton
                                  />
                                  <label class="k-radio-label ms-1" for="perform-mode-nondate">Non Date</label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Performed (Date-Time) (Shown only in Date mode) -->
                      @if (_form.get('timeInterval')?.value) {
                        <div class="col-12 margin-top-2">
                          <div class="form-group special_style_formgroup">
                            <div class="row align-items-center">
                              <div class="col-3 col-xl-2">
                                <label class="control-label">Performed *</label>
                              </div>
                              <div class="col-9 col-xl-10 transaction-move-input-fields">
                                <kendo-datetimepicker
                                  class="k-datepicker datepickerControlrightMargin bordered newFormControlWidth"
                                  formControlName="performDate"
                                  placeholder="Set Date and Time"
                                  [format]="_datePickerFormat"
                                  required
                                ></kendo-datetimepicker>
                              </div>
                            </div>
                            @if (_form.get('performDate')?.touched && _form.get('performDate')?.errors?.['required']) {
                              <div
                                class="row"
                                >
                                <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                  Performed date and time is required.
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      }
                      <!-- Measure when performed (Only shown in Non Date mode) -->
                      @if (!_form.get('timeInterval')?.value) {
                        <div class="col-12 margin-top-2">
                          <div class="form-group special_style_formgroup">
                            <div class="row align-items-center">
                              <div class="col-3 col-xl-2">
                                <label class="control-label">Measure when performed *</label>
                              </div>
                              <div class="col-9 col-xl-10 transaction-move-input-fields">
                                <kendo-combobox
                                  #Kendo_Combobox_MeasureWhenPerformed
                                  class="bordered newFormControlWidth"
                                  formControlName="measureWhenPerformedId"
                                  [data]="measuresWhenPerformedFiltered"
                                  [textField]="'name'"
                                  [valueField]="'id'"
                                  [valuePrimitive]="true"
                                  [suggest]="true"
                                  [filterable]="true"
                                  (filterChange)="handleMeasureFilter($event)"
                                  placeholder="Choose Measure"
                                  required
                                  >
                                  <ng-template kendoComboBoxNoDataTemplate>
                                    <div class="stocksettEnabledContainer">
                                      <span class="missing-item-txt">No measure found.</span>
                                    </div>
                                  </ng-template>
                                  <ng-template kendoComboBoxItemTemplate let-dataItem>
                                    <div class="transactions-receive-stock-dropdown-item">
                                      <div class="truncate">
                                        <span>{{ dataItem.name }}</span>
                                      </div>
                                    </div>
                                  </ng-template>
                                </kendo-combobox>
                              </div>
                            </div>
                            @if (_form.get('measureWhenPerformedId')?.touched && _form.get('measureWhenPerformedId')?.errors?.['required']) {
                              <div
                                class="row"
                                >
                                <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                  Measure when performed is required.
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      }
                      <!-- Open Schedule (Optional) -->
                      @if (openSchedules.length > 0 || _form.get('maintenanceAssetScheduleId')?.value || selectedSchedule || isPerformAction) {
                        <div class="col-12 margin-top-2">
                          <div class="form-group special_style_formgroup">
                            <div class="row">
                              <div class="col-3 col-xl-2">
                                <label class="control-label">Schedule</label>
                              </div>
                              <div class="col-9 col-xl-10 transaction-move-input-fields">
                                <kendo-combobox
                                  class="bordered newFormControlWidth"
                                  [data]="openSchedulesFiltered"
                                  [suggest]="true"
                                  [filterable]="true"
                                  (filterChange)="handleScheduleFilter($event)"
                                  placeholder="Select Open Schedule (Optional)"
                                  formControlName="maintenanceAssetScheduleId"
                                  textField="displayTitle"
                                  valueField="id"
                                  [valuePrimitive]="true"
                                  >
                                  <ng-template kendoComboBoxNoDataTemplate>
                                    <div class="stocksettEnabledContainer">
                                      <span class="missing-item-txt">No schedule found.</span>
                                    </div>
                                  </ng-template>
                                  <ng-template kendoComboBoxItemTemplate let-dataItem>
                                    <div class="transactions-receive-stock-dropdown-item">
                                      <div class="truncate">
                                        <strong>{{ dataItem.maintenanceCode || dataItem.maintenanceTypeName || ('#' + dataItem.id) }}</strong>
                                        @if (dataItem.maintenanceTypeName && dataItem.maintenanceCode) {
                                          <span> | {{ dataItem.maintenanceTypeName }}</span>
                                        }
                                        @if (dataItem.assetNumber) {
                                          <span> [Asset: {{ dataItem.assetNumber }}]</span>
                                        }
                                        @if (dataItem.scheduledFor) {
                                          <span> (Due: {{ dataItem.scheduledFor | dateFormat }})</span>
                                        }
                                      </div>
                                    </div>
                                  </ng-template>
                                </kendo-combobox>
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                      <!-- Performed By Person -->
                      <div class="col-12 margin-top-2">
                        <div class="form-group special_style_formgroup">
                          <div class="row">
                            <div class="col-3 col-xl-2">
                              <label class="control-label">Performed By *</label>
                            </div>
                            <div class="col-9 col-xl-10 transaction-move-input-fields">
                              <kendo-combobox
                                class="bordered newFormControlWidth"
                                [data]="personsDataFiltered"
                                [suggest]="true"
                                [filterable]="true"
                                (filterChange)="handlePersonFilter($event)"
                                placeholder="Choose a Person"
                                formControlName="personId"
                                textField="displayName"
                                valueField="id"
                                [valuePrimitive]="true"
                                required
                                >
                                <ng-template kendoComboBoxNoDataTemplate>
                                  <div class="stocksettEnabledContainer">
                                    <span class="missing-item-txt">No data found.</span>
                                  </div>
                                </ng-template>
                                <ng-template kendoComboBoxItemTemplate let-dataItem>
                                  <div class="transactions-receive-stock-dropdown-item">
                                    <div class="truncate">
                                      <strong>{{ dataItem.code || dataItem.fullName }}</strong>
                                      @if (dataItem.firstName || dataItem.lastName) {
                                        <span>
                                          ({{ dataItem.firstName }} {{ dataItem.lastName }})
                                        </span>
                                      }
                                    </div>
                                  </div>
                                </ng-template>
                              </kendo-combobox>
                              @if (_pageState !== PageState.VIEW) {
                                <span class="asap-input-icon">
                                  <img
                                    class="searchIcon"
                                    (click)="_openPersonsList()"
                                    [src]="'/assets/menu/' + _theme + '/search-icon.svg'"
                                    title="Search Person from list"
                                    />
                                  </span>
                                }
                                @if (_pageState !== PageState.VIEW) {
                                  <span class="asap-input-icon">
                                    <img
                                      class="addIcon"
                                      (click)="_openPersonsList()"
                                      [src]="'/assets/menu/' + _theme + '/add-new-icon_grid.svg'"
                                      title="Select Person from list"
                                      />
                                    </span>
                                  }
                                </div>
                              </div>
                              @if (_form.get('personId')?.touched && _form.get('personId')?.errors?.['required']) {
                                <div
                                  class="row"
                                  >
                                  <div class="offset-3 offset-xl-2 col-9 col-xl-10 text-danger small mt-1">
                                    Performed By is required.
                                  </div>
                                </div>
                              }
                            </div>
                          </div>
                          <!-- Recurrence Advance Checkbox -->
                          @if (selectedSchedule?.isRecurring) {
                            <div class="col-12 margin-top-2">
                              <div class="form-group special_style_formgroup">
                                <div class="row align-items-center">
                                  <div class="col-3 col-xl-2">
                                    <label class="control-label">Recurrence</label>
                                  </div>
                                  <div class="col-9 col-xl-10 d-flex align-items-center">
                                    <input
                                      type="checkbox"
                                      class="k-checkbox"
                                      id="advanceRecurrence"
                                      formControlName="advanceRecurrence"
                                      kendoCheckBox
                                      />
                                      <label class="k-checkbox-label ms-2 small" for="advanceRecurrence" style="cursor: pointer;">
                                        Advance schedule to next recurrence interval automatically
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    </div>
                    <!-- Right Panel: Costs & Notes -->
                    <div class="col-12 col-lg-6 small-pad-left">
                      <div [ngClass]="isCostExpanded ? 'transactionWidget' : 'transactionWidget-collapse'">
                        <div class="row">
                          <div class="col-12 sectionheader special_style_formgroup">
                            <span class="titleStandardStyle">Costs & Notes</span>
                            <span
                              [ngClass]="isCostExpanded ? 'k-icon k-i-sort-asc-sm' : 'k-icon k-i-sort-desc-sm'"
                              class="float-right pointer-click"
                              (click)="isCostExpanded = !isCostExpanded"
                            ></span>
                          </div>
                          @if (isCostExpanded) {
                            <!-- Labor Cost -->
                            <div class="col-12 margin-top-2">
                              <div class="form-group special_style_formgroup">
                                <div class="row">
                                  <div class="col-3 col-xl-2">
                                    <label class="control-label">Labor Cost</label>
                                  </div>
                                  <div class="col-9 col-xl-10 transaction-move-input-fields">
                                    <kendo-numerictextbox
                                      class="bordered newFormControlWidth"
                                      formControlName="laborCost"
                                      [min]="0"
                                      [format]="'n2'"
                                      placeholder="0.00"
                                    ></kendo-numerictextbox>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Parts Cost -->
                            <div class="col-12 margin-top-2">
                              <div class="form-group special_style_formgroup">
                                <div class="row">
                                  <div class="col-3 col-xl-2">
                                    <label class="control-label">Parts Cost</label>
                                  </div>
                                  <div class="col-9 col-xl-10 transaction-move-input-fields">
                                    <kendo-numerictextbox
                                      class="bordered newFormControlWidth"
                                      formControlName="partsCost"
                                      [min]="0"
                                      [format]="'n2'"
                                      placeholder="0.00"
                                    ></kendo-numerictextbox>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Total Cost Display -->
                            <div class="col-12 margin-top-2">
                              <div class="form-group special_style_formgroup">
                                <div class="row align-items-center">
                                  <div class="col-3 col-xl-2">
                                    <label class="control-label fw-bold">Total Cost</label>
                                  </div>
                                  <div class="col-9 col-xl-10">
                                    <span class="fw-bold fs-5 text-success">
                                      {{ totalCost | number: '1.2-2' }}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="section-separator"></div>
                            <!-- Notes -->
                            <div class="col-12 margin-top-2">
                              <div class="form-group special_style_formgroup">
                                <div class="row">
                                  <div class="col-3 col-xl-2">
                                    <label class="control-label">Notes</label>
                                  </div>
                                  <div class="col-9 col-xl-10 transaction-textarea-fields">
                                    <textarea
                                      class="bordered"
                                      rows="3"
                                      formControlName="notes"
                                      placeholder="Service details, parts replaced, or observations..."
                                      [readonly]="_pageState === PageState.VIEW"
                                    ></textarea>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Maintenance Notes (if schedule selected) -->
                            @if (selectedSchedule?.notes) {
                              <div class="col-12 margin-top-2">
                                <div class="form-group special_style_formgroup">
                                  <div class="row">
                                    <div class="col-3 col-xl-2">
                                      <label class="control-label">Schedule Note</label>
                                    </div>
                                    <div class="col-9 col-xl-10">
                                      <div class="p-2 bg-light rounded text-muted small border">
                                        {{ selectedSchedule?.notes }}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- TabStrip & Notes Sidebar Layout -->
                  <div class="example-wrapper">
                    <div class="row no_row_margin">
                      <div class="col-12 col-lg-9 col-xl-10 responsive_style numericboxtab" #kendo_tabstrip_wrapper>
                        <kendo-tabstrip (tabSelect)="_onTabSelect($event)" [tabPosition]="_kendoTabStripPosition">
                          <!-- Documents Tab -->
                          <kendo-tabstrip-tab #DocumentsTab [selected]="_selectedTab === 'Documents'">
                            <ng-template kendoTabTitle>Documents</ng-template>
                            <ng-template kendoTabContent>
                              <asap-document-manager
                                [(documents)]="_performedFilesDoc"
                                [pageState]="_pageState"
                                entityTitle="Perform Maintenance"
                              ></asap-document-manager>
                            </ng-template>
                          </kendo-tabstrip-tab>
                        </kendo-tabstrip>
                      </div>
                      <!-- Notes Right Sidebar -->
                      <div class="col-12 col-lg-3 col-xl-2 responsive_style">
                        <div class="borderedNotesContainer">
                          <note
                            #note
                            [_pageState]="_pageState"
                            [_model]="_model"
                            [_page]="_page"
                          ></note>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
                <!-- Asset Popup Selector (matching Reserve Popup addFromLibrary pattern) -->
                @if (_assetsListEnabled) {
                  <kendo-window
                    title="Select Asset"
                    (close)="_closeAssetsList()"
                    [resizable]="false"
                    [minHeight]="120"
                    class="addFromLibrary"
                    >
                    <div class="greytabtop">
                      <button kendoButton class="topRightButtonSelect" (click)="_returnSelectedAssetsHandler($event)">
                        Select & Return
                      </button>
                    </div>
                    <asap-grid-layout
                      [singleSelect]="true"
                      [selectable]="true"
                      [groupingEnabled]="true"
                      [pagingEnabled]="true"
                      [headerEnable]="true"
                      [headerTitle]="'Select An Asset'"
                      [entityName]="'asset'"
                      [addNewFormEnabled]="false"
                      [enableSiteSelection]="false"
                      [orderEnable]="true"
                      [sortingEnabled]="true"
                      [page]="'assets'"
                      [deletedEnabled]="false"
                      [selectByField]="'id'"
                      [selectByFieldTitle]="''"
                      [filteringEnable]="true"
                      [HideNotesFields]="true"
                      [resolver]="_getAssets"
                      (onRowSelectionChanges)="_onAssetsRowSelectionChanges($event)"
                      >
                      <ng-template pageListColumn [field]="'assetNumber'" [name]="'Asset #'" let-item>
                        {{ item.assetNumber }}
                      </ng-template>
                      <ng-template pageListColumn [field]="'name'" [name]="'Asset Name'" let-item>
                        {{ item.name }}
                      </ng-template>
                      <ng-template pageListColumn [field]="'site.code'" [name]="'Site'" let-item>
                        {{ item.site?.code }}
                      </ng-template>
                      <ng-template pageListColumn [field]="'location.code'" [name]="'Location'" let-item>
                        {{ item.location?.code }}
                      </ng-template>
                    </asap-grid-layout>
                  </kendo-window>
                }
                <!-- Person Popup Selector (matching Reserve Popup addFromLibrary pattern) -->
                @if (_personsListEnabled) {
                  <kendo-window
                    title="Select Person"
                    (close)="_closePersonsList()"
                    class="addFromLibrary"
                    >
                    <div class="greytabtop">
                      <button kendoButton class="topRightButtonSelect" (click)="_returnSelectedPersonsHandler($event)">
                        Select & Return
                      </button>
                    </div>
                    <asap-grid-layout
                      [selectable]="true"
                      [singleSelect]="true"
                      [groupingEnabled]="true"
                      [pagingEnabled]="true"
                      [headerEnable]="true"
                      [headerTitle]="'Select Person'"
                      [entityName]="'person'"
                      [addNewFormEnabled]="false"
                      [orderEnable]="true"
                      [sortingEnabled]="true"
                      [page]="'persons'"
                      [deletedEnabled]="false"
                      [selectByField]="'id'"
                      [selectByFieldTitle]="'Select Person'"
                      [enableSiteSelection]="false"
                      [filteringEnable]="true"
                      [HideNotesFields]="true"
                      [resolver]="_getPersons"
                      (onRowSelectionChanges)="_onPersonsRowSelectionChanges($event)"
                      >
                      <ng-template pageListColumn [field]="'code'" [name]="'Code'" let-item>
                        {{ item.code }}
                      </ng-template>
                      <ng-template pageListColumn [field]="'firstName'" [name]="'First Name'" let-item>
                        {{ item.firstName }}
                      </ng-template>
                      <ng-template pageListColumn [field]="'lastName'" [name]="'Last Name'" let-item>
                        {{ item.lastName }}
                      </ng-template>
                    </asap-grid-layout>
                  </kendo-window>
                }
                @if (_assetsListEnabled || _personsListEnabled || _isDialiog) {
                  <div class="k-overlay"></div>
                }
                <!-- Maintenance Type Dialog (Enterprise V2 System Dialog) -->
                @if (_isDialiog) {
                  <asap-dialog-layout
                    [componentName]="_dialogComponentName"
                    [data]="_dialogModel"
                    (closeDialog)="_closeDialog($event)"
                  ></asap-dialog-layout>
                }
              </kendo-window>
            }
